import type {
  DurationTieredPricing,
  ParamPriceTiers,
  PriceUnit,
  TextLengthTieredPricing,
} from '../../../../shared-generation-pricing-config'

/**
 * Single source of truth for generation-model price formatting, shared by
 * GenerationModelSelector and the model detail page so the numbers/labels
 * can never drift between views.
 */

export function formatCost(priceMillidollars: number): string {
  return `$${(priceMillidollars / 1000).toFixed(2)}`
}

export function formatPriceUnit(unit: PriceUnit): string {
  switch (unit) {
    case 'per-image': return '/ img'
    case 'per-video': return '/ vid'
    case 'per-second-video': return '/ sec'
    case 'per-second-audio': return '/ sec'
    case 'per-1k-characters': return '/ 1K chars'
    case 'per-request': return '/ req'
  }
}

export const GENERATION_FAMILY_LABELS: Record<string, string> = {
  google: 'Google',
  bytedance: 'Bytedance',
  kling: 'Kling',
  wan: 'Wan',
  grok: 'Grok',
  openai: 'OpenAI',
  elevenlabs: 'ElevenLabs',
  replicate: 'Replicate',
  stability: 'Stability AI',
  unknown: 'Other',
}

type GenerationPricing = number | Record<string, ParamPriceTiers> | DurationTieredPricing | TextLengthTieredPricing

export function isDurationTieredPricing(p: GenerationPricing): p is DurationTieredPricing {
  return typeof p === 'object' && p !== null && 'kind' in p && p.kind === 'duration'
}

export function isTextLengthTieredPricing(p: GenerationPricing): p is TextLengthTieredPricing {
  return typeof p === 'object' && p !== null && 'kind' in p && p.kind === 'text-length'
}

/** Humanize a tier param's raw value, e.g. sound: 'true' -> 'Sound on'. */
export function humanizeTierValue(param: string, value: string): string {
  if (param === 'sound') return value === 'true' ? 'Sound on' : 'Sound off'
  if (value === 'true' || value === 'false') return `${humanizeLabel(param)}: ${value === 'true' ? 'On' : 'Off'}`
  return value
}

export function humanizeLabel(raw: string): string {
  return raw
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
